import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { X, User, Calendar, Trophy, Target, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export default function StudentDetailModal({ studentId, onClose }) {
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('basic'); // basic, records, tasks, awards

  useEffect(() => {
    loadStudentData();
  }, [studentId]);

  const loadStudentData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/students/${studentId}/full-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStudentData(res.data);
    } catch (error) {
      console.error('載入學生資料失敗:', error);
      toast.error('載入學生資料失敗');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  if (!studentData) {
    return null;
  }

  const { student, class_records, task_completions, awards, stats } = studentData;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-blue-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-blue-600 text-2xl font-bold">
                {student.username?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-2xl font-bold">{student.username}</h2>
                <p className="text-blue-100">{student.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <p className="text-sm text-blue-100">總課堂數</p>
              <p className="text-2xl font-bold">{stats.total_classes}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <p className="text-sm text-blue-100">總積分</p>
              <p className="text-2xl font-bold">{stats.total_points}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <p className="text-sm text-blue-100">總獎項</p>
              <p className="text-2xl font-bold">{stats.total_awards}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'basic'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <User size={18} className="inline mr-2" />
            基本資料
          </button>
          <button
            onClick={() => setActiveTab('records')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'records'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Calendar size={18} className="inline mr-2" />
            課堂紀錄 ({class_records.length})
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'tasks'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Target size={18} className="inline mr-2" />
            任務完成 ({task_completions.length})
          </button>
          <button
            onClick={() => setActiveTab('awards')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'awards'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Trophy size={18} className="inline mr-2" />
            獲獎紀錄 ({awards.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">用戶名</p>
                  <p className="font-medium">{student.username}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">電子郵件</p>
                  <p className="font-medium">{student.email}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">電話</p>
                  <p className="font-medium">{student.phone || '未提供'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">性別</p>
                  <p className="font-medium">{student.gender === 'male' ? '男' : student.gender === 'female' ? '女' : '未提供'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">訓練地點</p>
                  <p className="font-medium">{student.location || '未提供'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">帳號狀態</p>
                  <p className="font-medium">{student.account_status === 'approved' ? '已批准' : '待審核'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Class Records Tab */}
          {activeTab === 'records' && (
            <div className="space-y-3">
              {class_records.length === 0 ? (
                <p className="text-center text-gray-500 py-8">暫無課堂紀錄</p>
              ) : (
                class_records.map((record) => (
                  <div key={record.record_id} className="p-4 bg-white border rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-lg">{record.date}</span>
                      {record.time && <span className="text-sm text-gray-500">{record.time}</span>}
                    </div>
                    {record.notes && <p className="text-sm text-gray-600 mb-2">{record.notes}</p>}
                    {record.video_url && (
                      <div className="mt-2">
                        <video
                          src={record.video_url}
                          controls
                          className="w-full max-h-64 rounded"
                        />
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Tasks Tab */}
          {activeTab === 'tasks' && (
            <div className="space-y-3">
              {task_completions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">暫無任務完成紀錄</p>
              ) : (
                task_completions.map((completion) => (
                  <div key={completion.completion_id} className="p-4 bg-white border rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium">任務 ID: {completion.task_id}</p>
                      <p className="text-sm text-gray-500">
                        完成時間: {new Date(completion.completed_at).toLocaleString('zh-TW')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">+{completion.points_earned}</p>
                      <p className="text-xs text-gray-500">積分</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Awards Tab */}
          {activeTab === 'awards' && (
            <div className="grid grid-cols-2 gap-4">
              {awards.length === 0 ? (
                <div className="col-span-2">
                  <p className="text-center text-gray-500 py-8">暫無獲獎紀錄</p>
                </div>
              ) : (
                awards.map((award) => (
                  <div key={award.award_id} className="p-4 bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <Trophy size={24} className="text-yellow-600" />
                      <h4 className="font-bold text-lg">{award.award_name}</h4>
                    </div>
                    {award.description && (
                      <p className="text-sm text-gray-600 mb-2">{award.description}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      頒發時間: {new Date(award.awarded_at).toLocaleDateString('zh-TW')}
                    </p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <Button onClick={onClose} variant="outline" className="w-full">
            關閉
          </Button>
        </div>
      </div>
    </div>
  );
}
